import styled from '@emotion/styled';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import {
  CncReportLocal,
  Indicator,
  ParameterInput,
  Pie1DData,
} from '@gameonly/core';
import Button, { BlueButton } from '../ui/Button';
import { ReactComponent as ExportIcon } from '../ui/icons/export.svg';
import useReport from './context/useReport';
import CreateLocal from './CreateLocal';
import DeleteLocal from './DeleteLocal';

import { Types } from 'mongoose';
import useUser from '../authentication/context/useUser';
import useModels from '../project/context/useModels';
import useProjects from '../project/context/useProjects';
import keyify from '../project/parameters/keyify';
import IndicatorComponent from '../results/IndicatorComponent';
import Pie1DComponent from '../results/Pie1DComponent';
import { ResultSection } from '../results/ResultsSimple';
import LoadingButton from '../ui/LoadingButton';
import Markdown from '../ui/Markdown';
import Checklist from './Checklist';
import CNCReportInstructions from './CNCReportInstructions';
import EditReportModal from './EditReportModal';
import HomologationCNC, { MarkdownModalButton } from './HomologationCNC';
import ReportInformation from './ReportInformation';
import { ValidationInformation } from './ValidateReportModal';

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  min-height: 100%;
  flex-grow: 1;
  position: relative;
  background-color: #ebf0f5;
  > * {
    z-index: 1;
  }
`;

const HomologationContainer = styled.div`
  width: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin: 16px 0;
`;

const ReportOverviewContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 16px;
  > * {
    flex: 1 1 0;
  }
  @media (min-width: 1200px) {
    width: 1200px;
  }
  }
`;

const ValidationContainer = styled.div`
  width: fit-content;
  background-color: var(--color-accent);
  display: flex;
  flex-direction: column;
  gap: 16px;
  border-radius: 8px;
  padding: 16px;
  z-index: 1300;
`;

const ResultContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const IndicatorContainer = styled.div`
  display: flex;
  align-items: flex-start;
  flex-direction: column;
`;

const PieContainer = styled.div`
  width: 80px;
  height: 80px;
`;

const TableHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Disk = styled.div`
  width: 16px;
  height: 16px;
  border-radius: 8px;
  background-color: ${(props) => props.color};
`;

const getNewProjectInitialValues = () => {
  return [
    {
      type: 'id',
      id: 'objectif_formulaire',
      value: 'Demande de subvention CNC (aide à la production)',
    },
  ] as ParameterInput[];
};

const CncReportComponent = () => {
  const { reportId } = useParams();
  const {
    report,
    createLocal,
    deleteLocal,
    editReport,
    downloadReport,
    loadingCreateLocal,
  } = useReport(reportId);
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation(['cnc-reports', 'common', 'model']);
  const { create, loading } = useProjects();
  const { user } = useUser();
  const { models } = useModels();

  const [dialogState, setDialogState] = useState({
    createOpen: false,
    editOpen: false,
    deleteOpen: false,
    deleteLocalId: null as Types.ObjectId | null,
  });

  if (!report) return null;

  const siteModel = models?.find((m) => m.type === 'site');

  const handleAction = async (
    action: 'create' | 'delete' | 'edit' | 'createProject',
    payload?: any,
  ) => {
    try {
      if (action === 'create') {
        await createLocal(payload);
        setDialogState((prev) => ({ ...prev, createOpen: false }));
      } else if (action === 'createProject') {
        const newProject = await create({
          type: 'site',
          values: getNewProjectInitialValues(),
        });
        const projectName = siteModel
          ? t(`model:${keyify(siteModel.name)}.defaultTitle`)
          : '';
        const local: CncReportLocal = {
          id: newProject._id,
          name: projectName,
          type: 'internal',
          data: {
            etp: 0,
            studio: user?.company || '',
          },
          validated: false,
        };
        await createLocal({ local });
      } else if (action === 'delete' && dialogState.deleteLocalId) {
        await deleteLocal({ localId: dialogState.deleteLocalId });
        setDialogState((prev) => ({
          ...prev,
          deleteOpen: false,
          deleteLocalId: null,
        }));
      } else if (action === 'edit') {
        await editReport(payload);
        setDialogState((prev) => ({ ...prev, editOpen: false }));
      }
    } catch (err) {
      enqueueSnackbar(t('common:error.generic'), { variant: 'error' });
    }
  };

  const locals = report.locals;
  const validatedLocals = locals.filter((local) => local.validated);

  const pieData = validatedLocals
    .map((local, index) => ({
      name: local.name,
      value: local.data.totalCO2,
      fill: colors[index % colors.length],
    }))
    .filter(
      (p) => typeof p.value === 'number' && p.value !== undefined,
    ) as Pie1DData[];

  const indicator: Indicator = {
    type: 'indicator',
    code: 'co2',
    title: t('cnc-reports:dashboard.indicator.co2-title'),
    number: validatedLocals
      .reduce(
        (total, local) =>
          local.data.totalCO2 ? local.data.totalCO2 + total : total,
        0,
      )
      .toFixed(0),
    unit: t('cnc-reports:dashboard.indicator.co2-unit'),
  };

  const isValidated = !!report.validatedAt;

  if (!locals.length) {
    return (
      <>
        {dialogState.createOpen && (
          <CreateLocal
            open={dialogState.createOpen}
            onClose={() =>
              setDialogState({ ...dialogState, createOpen: false })
            }
            onSave={(local) => handleAction('create', { local })}
            loading={loadingCreateLocal}
          />
        )}
        <NoLocals
          onAdd={() => setDialogState({ ...dialogState, createOpen: true })}
          onNew={() => handleAction('createProject')}
          loading={loading || loadingCreateLocal}
        />
      </>
    );
  }

  return (
    <Container>
      {dialogState.createOpen && (
        <CreateLocal
          open={dialogState.createOpen}
          onClose={() => setDialogState({ ...dialogState, createOpen: false })}
          onSave={(local) => handleAction('create', { local })}
          loading={loadingCreateLocal}
        />
      )}
      {dialogState.deleteOpen && (
        <DeleteLocal
          open={dialogState.deleteOpen}
          onClose={() => setDialogState({ ...dialogState, deleteOpen: false })}
          onConfirm={() => handleAction('delete')}
        />
      )}
      {dialogState.editOpen && (
        <EditReportModal
          open={dialogState.editOpen}
          report={report}
          onClose={() => setDialogState({ ...dialogState, editOpen: false })}
          onSave={(reportData) => handleAction('edit', reportData)}
        />
      )}
      <HomologationContainer>
        <HomologationCNC />
      </HomologationContainer>
      <Header>
        <p className="h2b">
          {t('cnc-reports:dashboard.title', { reportName: report.name })}
        </p>
        <Chip
          label={t(`cnc-reports:creation-modal.type-${report.type}`, {
            reportName: report.name,
          })}
          color="secondary"
          sx={{
            textTransform: 'uppercase',
            fontWeight: 'bold',
            backgroundColor: 'var(--color-accent)',
            color: 'black',
          }}
        />
        {isValidated && (
          <Button
            color="primary"
            onClick={downloadReport}
            startIcon={<ExportIcon />}
          >
            {t('cnc-reports:dashboard.export')}
          </Button>
        )}
      </Header>

      <ReportOverviewContainer>
        {isValidated && (
          <ValidationContainer>
            <ValidationInformation report={report} type="report" />
          </ValidationContainer>
        )}

        {!isValidated && (
          <OverviewSection
            title={t('cnc-reports:dashboard.instruction.title')}
            backgroundColor="var(--color-primary-light)"
            width="300px"
          >
            <CNCReportInstructions />
          </OverviewSection>
        )}

        <OverviewSection
          title={t('cnc-reports:dashboard.information.title')}
          backgroundColor="lightgrey"
          width="300px"
        >
          <ReportInformation
            report={report}
            onEdit={() => setDialogState({ ...dialogState, editOpen: true })}
          />
        </OverviewSection>

        {!isValidated && (
          <OverviewSection title={'Checklist'} backgroundColor="lightgrey">
            <Checklist report={report} isValidated={isValidated} />
          </OverviewSection>
        )}

        {indicator && Number(indicator.number) > 0 && (
          <OverviewSection
            title={t('cnc-reports:dashboard.local-footprint')}
            rightComponent={
              <MarkdownModalButton
                modalTitle={t(
                  'cnc-reports:dashboard.local-footprint-info-title',
                )}
                markdownContent={t(
                  'cnc-reports:dashboard.local-footprint-markdown-content',
                )}
                buttonVariant="icon"
              />
            }
            backgroundColor="var(--color-primary-light)"
            hideOnSmallScreen
          >
            <ResultContainer>
              <IndicatorContainer>
                <ResultSection result={indicator} loading={false}>
                  <IndicatorComponent result={indicator} />
                </ResultSection>
              </IndicatorContainer>
              <PieContainer>
                <Pie1DComponent
                  result={{ type: 'pie1D', data: pieData, unit: 'kgCO2eq' }}
                  noTranslationForTooltip
                />
              </PieContainer>
            </ResultContainer>
          </OverviewSection>
        )}
      </ReportOverviewContainer>

      <TableHeader>
        {locals.length > 0 && (
          <p className="h5b">
            {t('cnc-reports:dashboard.nb-locals', {
              nbLocal: locals.length,
            })}{' '}
          </p>
        )}
        {!isValidated && (
          <BlueButton
            color="primary"
            onClick={() => setDialogState({ ...dialogState, createOpen: true })}
            startIcon={<AddIcon />}
          >
            {t('cnc-reports:dashboard.add-local')}
          </BlueButton>
        )}
      </TableHeader>

      <Table
        sx={{
          backgroundColor: 'white',
          borderCollapse: 'unset',
          border: '2px solid var(--pink)',
        }}
      >
        <TableHead>
          <TableRow>
            <TableCell>{t('cnc-reports:dashboard.table.name')}</TableCell>
            <TableCell>{t('cnc-reports:dashboard.table.studio')}</TableCell>
            <TableCell>{t('cnc-reports:dashboard.table.type')}</TableCell>
            <TableCell>{t('cnc-reports:dashboard.table.impact')}</TableCell>
            <TableCell>{t('cnc-reports:dashboard.table.etp')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {locals.map((local, index) => (
            <LocalRow
              key={local.id.toString()}
              local={local}
              isValidated={isValidated}
              onDelete={(id) =>
                setDialogState({
                  ...dialogState,
                  deleteOpen: true,
                  deleteLocalId: id,
                })
              }
              color={colors[index % colors.length]}
            />
          ))}
        </TableBody>
      </Table>
    </Container>
  );
};

const NoLocalsContainer = styled.div`
  padding: 32px;
  background-color: white;
  border-radius: 8px;
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 32px;
  max-width: 800px;
  margin-top: 50px;
`;

const MarkdownContainer = styled.div`
  text-align: center;
  align-items: center;
  display: flex;
  flex-direction: column;
`;

const NoLocals = ({
  onAdd,
  onNew,
  loading,
}: {
  onAdd: () => void;
  onNew: () => void;
  loading: boolean;
}) => {
  const { t } = useTranslation(['cnc-reports', 'common']);
  return (
    <NoLocalsContainer>
      <MarkdownContainer>
        <Markdown>{t('cnc-reports:dashboard.no-local-markdown-text')}</Markdown>
      </MarkdownContainer>
      <Button onClick={onAdd} startIcon={<AddIcon />} color="primary">
        {t('cnc-reports:dashboard.add-local')}
      </Button>
      <LoadingButton
        onClick={onNew}
        startIcon={<AddIcon />}
        color="primary"
        loading={loading}
      >
        {t('cnc-reports:dashboard.new-local')}
      </LoadingButton>
    </NoLocalsContainer>
  );
};

const LocalRow = ({
  local,
  onDelete,
  color,
  isValidated,
}: {
  local: CncReportLocal;
  onDelete: (id: Types.ObjectId) => void;
  color: string;
  isValidated: boolean;
}) => {
  const { t } = useTranslation(['common', 'cnc-reports']);
  const navigate = useNavigate();
  if (local.validated) {
    return (
      <TableRow key={local.id.toString()} hover>
        <TableCell>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Disk color={color} />
            {local.name}
            {local.type === 'internal' && (
              <Button
                color="secondary"
                onClick={() => navigate(`/project/${local.id.toString()}`)}
              >
                {t('common:open')}
              </Button>
            )}
            {!isValidated && (
              <IconButton onClick={() => onDelete(local.id)}>
                <DeleteIcon />
              </IconButton>
            )}
          </Box>
        </TableCell>
        <TableCell>{local.data.studio}</TableCell>
        <TableCell>
          {local.type === 'internal'
            ? t('cnc-reports:local-type.internal')
            : t('cnc-reports:local-type.partner-studio')}
        </TableCell>
        <TableCell>{local.data.totalCO2}</TableCell>
        <TableCell>{local.data.etp}</TableCell>
      </TableRow>
    );
  }
  return (
    <TableRow key={local.id.toString()} hover>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Disk color={color} />
          {local.name}
          <Button
            color="primary"
            onClick={() => navigate(`/project/${local.id.toString()}`)}
          >
            {t('common:open_and_validate')}
          </Button>
          {!isValidated && (
            <IconButton onClick={() => onDelete(local.id)}>
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      </TableCell>
      <TableCell>{local.data.studio}</TableCell>
      <TableCell>
        {local.type === 'internal'
          ? t('cnc-reports:local-type.internal')
          : t('cnc-reports:local-type.partner-studio')}
      </TableCell>
      <TableCell>-</TableCell>
      <TableCell>{local.data.etp}</TableCell>
    </TableRow>
  );
};

const colors = [
  '#e67373',
  '#e69573',
  '#e6b873',
  '#e6da73',
  '#cfe673',
  '#ace673',
  '#8ae673',
  '#73e67e',
  '#73e6a1',
  '#73e6c3',
  '#73e6e6',
  '#73c3e6',
  '#73a1e6',
  '#737ee6',
  '#8a73e6',
  '#ac73e6',
  '#cf73e6',
  '#e673da',
  '#e673b8',
  '#e67395',
];

interface OverviewContainerProps {
  backgroundColor: string;
  width: string | undefined;
  hideOnSmallScreen?: boolean;
}

const OverviewContainer = styled.div<OverviewContainerProps>`
  min-width: ${(props) => props.width || '250px'};
  padding: 16px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  background-color: ${(props) => props.backgroundColor};
  ${(props) =>
    props.hideOnSmallScreen &&
    `
    @media (max-width: 1200px) {
      display: none;
    }
  `}
`;

const HeaderContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const OverviewSection = ({
  title,
  rightComponent,
  children,
  backgroundColor,
  width,
  hideOnSmallScreen,
}: {
  title: string;
  rightComponent?: React.ReactNode;
  children: React.ReactNode;
  backgroundColor: string;
  width?: string;
  hideOnSmallScreen?: boolean;
}) => (
  <OverviewContainer
    backgroundColor={backgroundColor}
    width={width}
    hideOnSmallScreen={hideOnSmallScreen}
  >
    <HeaderContainer>
      <p className="h5r">{title}</p>
      {rightComponent && <div>{rightComponent}</div>}
    </HeaderContainer>
    {children}
  </OverviewContainer>
);

export default CncReportComponent;
